import { useEffect, type RefObject } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * v2.3 P1.4: what a modal owes the keyboard.
 *
 * - focus lands in it on open: the element marked `data-autofocus`, else the first focusable one;
 * - Tab and Shift+Tab cycle inside it and never escape to the page behind;
 * - Esc closes it from ANYWHERE — the listener is on the document, because the old handler sat on the
 *   dialog's own div and a dialog opened by a drag has nothing focused inside it, so Esc did nothing
 *   until the user clicked in.
 */
export function useModalKeys(ref: RefObject<HTMLElement | null>, onClose: () => void): void {
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const first =
      root.querySelector<HTMLElement>("[data-autofocus]") ?? root.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
    if (first instanceof HTMLInputElement) first.select();

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !root) return;
      const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const head = items[0]!;
      const tail = items[items.length - 1]!;
      const active = document.activeElement;
      if (!root.contains(active)) {
        e.preventDefault();
        head.focus();
      } else if (e.shiftKey && active === head) {
        e.preventDefault();
        tail.focus();
      } else if (!e.shiftKey && active === tail) {
        e.preventDefault();
        head.focus();
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
    // onClose is a fresh closure each render; the dialog's lifetime is what matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);
}

/** Dollars as typed ("45k", "$1,000,000", "47500.50") → integer cents, or null when not a number. */
export function parseDollars(raw: string): number | null {
  const s = raw.trim().toLowerCase().replace(/[$,\s]/g, "");
  if (!s) return null;
  const m = /^(\d+(?:\.\d+)?)(k|m)?$/.exec(s);
  if (!m) return null;
  const mult = m[2] === "k" ? 1_000 : m[2] === "m" ? 1_000_000 : 1;
  return Math.round(Number(m[1]) * mult * 100);
}
